<script>
import ButtonSubmit from "./ButtonSubmit.vue";
import ButtonCancel from "./ButtonCancel.vue";
import api from "../api/api";
import { replaceTokens } from "../functions.js";
// import CryptoJS from "crypto-js";

export default {
  name: 'AddBackupEmailBox',
  components: { ButtonSubmit, ButtonCancel },
  props: {
    userData: { type: Object, required: true },
    brandData: { type: Object, required: true }
  },
  data() {
    return {
      toggle: false,
      emailInput: '',
      emails: [],
      selectedBackup: '',
      previousBackup: '',
      isLoading: false,
      loadingMessage: ''
    };
  },
  computed: {
    currentBackupEmail() {
      return this.selectedBackup;
    },
    verifiedEmails() {
      return this.emails.filter(e => e.verified);
    },
    unverifiedEmails() {
      return this.emails.filter(e => !e.verified);
    },
    primaryEmail() {
      const primary = this.emails.find(e => e.primary);
      return primary ? primary.email : '';
    },
  },
  async mounted() {
    try {
      await this.fetchEmails();
    } catch (error) {
      console.error('Initialization error:', error);
      alert('Failed to initialize email manager');
    }
  },
  methods: {

    async handleSetPrimaryEmail(email) {
      // Don't proceed if already primary or no change
      if (email === this.primaryEmail) return;

      if (!confirm(`Set ${email} as your primary email?`)) {
        return;
      }

      try {
        this.isLoading = true;

        // Call API to set primary email
        await api.post('/set-primary-email', {
          individual_id: this.userData.ind_id,
          email: email,
          modified_by: this.userData.ind_id
        });

        // Update local state
        this.emails = this.emails.map(e => ({
          ...e,
          primary: e.email === email
        }));

        alert('Primary email updated successfully');

      } catch (error) {
        console.error('Failed to update primary email:', error);
        alert('Error updating primary email');
      } finally {
        this.isLoading = false;
      }
    },

    async sendPasswordSetupEmail(newIndId, newEmail) {
      try {
        // First get the user data
        const res1 = await api.get(`individuals/emaildetails/${newIndId}`);
        const newUserResponse = res1.data;

        // Get the email template
        const templateResponse = await api.get(
          `email-templates/template-type/New associate email account/org/${newUserResponse.org_id ? newUserResponse.org_id : 1
          }/suborg/0/program/0`
        );

        let emailTemplateId, subject, email_body;

        // Normalize template data (handle array vs object)
        const templateData = Array.isArray(templateResponse.data)
          ? templateResponse.data[0] || {}
          : templateResponse.data || {};

        if (!templateData.email_body) {
          // Fallback template
          const defaultTemplateResponse = await api.get(
            "email-templates/template-type/New associate email account/org/0/suborg/0/program/0"
          );

          const defaultData = Array.isArray(defaultTemplateResponse.data)
            ? defaultTemplateResponse.data[0] || {}
            : defaultTemplateResponse.data || {};

          emailTemplateId = defaultData.email_template_id;
          subject = defaultData.subject || "";
          email_body = defaultData.email_body || "";
        } else {
          emailTemplateId = templateData.email_template_id;
          subject = templateData.subject || "";
          email_body = templateData.email_body || "";
        }

        // Get email details for the user
        const detailsResponse = await api.get(`individuals/emaildetails/${newIndId}`);
        const res = detailsResponse.data;

        // Build safe full name with fallback
        const fullName = `${res.first_name || ''} ${res.last_name || ''}`.trim() || 'User';

        // Set up tokens
        const tokens = {
          recipient_email: newEmail,
          website_url: res.website_url,
          program_name: res.program_name,
          website_sender_email: res.website_sender_email,
          user_full_name: fullName,
          brand_path: res.brand_path
        };


      // Generate secure password reset token for new user
      const tokenResponse = await api.post('create-password-reset-token', {
        ind_id: newIndId,
        email: newEmail,  // ← Add email (required by backend)
        expiration_days: 30   // ← Add expiration days (30 days for survey invitations)
      });
      const resetToken = tokenResponse.data.token;

      const password_link =
        "<a href=\"" +
        tokens.website_url +
        "#/set_password?token=" +
        encodeURIComponent(resetToken) +
        "\">Click Here</a>";

        // Replace tokens in subject and body
        const convertedSubject = replaceTokens(
          subject,
          tokens,
          password_link
        );
        const convertedBody = replaceTokens(
          email_body,
          tokens,
          password_link
        );

        // Prepare and send email
        const emailData = {
          send_from: tokens.website_sender_email,
          send_to: tokens.recipient_email,
          subject: convertedSubject,
          body: convertedBody,
          org_id: newUserResponse.org_id,
          email_template_id: emailTemplateId,
          ind_id: newIndId,
        };

        await api.post("sendemail", emailData);
        console.log('Password setup email sent successfully');

      } catch (error) {
        console.error('Error sending password setup email:', error);
        throw error;
      }
    },

    showForm() {
      this.toggle = !this.toggle;
    },

    async fetchEmails() {
      try {
        const res = await api.get(`get-associated-emails/${this.userData.ind_id}`);
        this.emails = res.data;

        if (this.emails.length > 0) {
          const backup = this.emails.find(e => !e.primary && e.verified) ||
            this.emails.find(e => !e.primary);
          this.selectedBackup = backup?.email || '';
          this.previousBackup = this.selectedBackup;
        }
      } catch (err) {
        console.error('Error fetching emails:', err);
        console.log('Failed to fetch associated emails');
      }
    },

    async handleAddEmail() {
      try {
        const newEmail = this.emailInput.trim();
        if (!newEmail) {
          alert('Please enter a valid email address');
          return;
        }

        this.isLoading = true;
        this.loadingMessage = 'Initializing...';

        // Step 0: Ensure primary email exists
        await api.post('/add-primary-email', {
          ind_id: this.userData.ind_id,
          email: this.userData.email,
          modified_by: this.userData.ind_id
        });

        // Step 1: Check if email exists
        this.loadingMessage = 'Checking email...';
        const { data: emailCheck } = await api.get(`/individuals-check-email-exist/${newEmail}`);
        let backupIndId;
        const emailExists = emailCheck.message === "exist";

        if (emailExists) {
          backupIndId = emailCheck.data.ind_id;

          if (!confirm(`Email ${newEmail} already exists. Link it to ${this.userData.email} as backup?`)) {
            this.isLoading = false;
            this.loadingMessage = '';
            return;
          }
        } else {
          // Step 2: Create new backup account
          this.loadingMessage = 'Creating new account...';
          const createResponse = await api.post('/create-backup-user-account', {
            email: newEmail,
            ind_id: this.userData.ind_id,
            created_by: this.userData.ind_id
          });
          backupIndId = createResponse.data.ind_id;

          // Step 3: Send password setup email
          this.loadingMessage = 'Sending password setup email...';
          try {
            await this.sendPasswordSetupEmail(backupIndId, newEmail);
            console.log('Password setup email sent successfully');
          } catch (err) {
            console.error('Failed to send password setup email:', err);
            // Optional: Decide if you still want to proceed with backup relationship + verification
          }
        }

        // Step 4: Add backup relationship
        this.loadingMessage = 'Adding backup relationship...';
        await api.post('/add-backup-email', {
          primary_ind_id: this.userData.ind_id,
          ind_id: backupIndId,
          email: newEmail,
          verified: 0,
          modified_by: this.userData.ind_id
        });

        // Step 5: Send verification email
        this.loadingMessage = 'Sending verification email...';
        let senderEmail = this.userData.brandData?.website_sender_email || 'help@talentsage.com';
        await api.post('/send-verification-email', {
          ind_id: this.userData.ind_id,
          email: newEmail,
          send_from: senderEmail
        });

        // Update UI
        this.emails.push({
          email: newEmail,
          verified: false,
          verificationSent: true,
          verificationDate: new Date().toISOString()
        });

        alert(emailExists
          ? `${newEmail} was linked to your account. Verification email sent.`
          : `Account created for ${newEmail}. Password setup and verification emails sent.`
        );

        this.resetEmailForm();

      } catch (error) {
        console.error('Error adding email:', error);
        if (error.response?.status === 400) {
          alert(error.response.data.message);
        } else {
          alert('Failed to add email. Please try again.');
        }
      } finally {
        this.isLoading = false;
        this.loadingMessage = '';
      }
    },

    async handleResend(email) {
      if (!confirm(`Resend verification email to ${email}?`)) return;

      try {
        this.isLoading = true;

        let senderEmail = this.userData.brandData?.website_sender_email || 'help@talentsage.com';
        await api.post('/send-verification-email', {
          ind_id: this.userData.ind_id,
          email: email,
          send_from: senderEmail
        });

        // Update verification status
        this.emails = this.emails.map(e => {
          if (e.email === email) {
            return { ...e, verificationSent: true, verificationDate: new Date().toISOString() };
          }
          return e;
        });

        alert(`Verification email resent to ${email}`);

      } catch (error) {
        console.error('Error resending verification:', error);
        alert('Failed to resend verification email');
      } finally {
        this.isLoading = false;
      }
    },

    async handleRemove(email) {
      if (!confirm(`Permanently remove ${email} from your account?`)) return;

      try {
        this.isLoading = true;

        // Verify email is not primary
        if (email === this.primaryEmail) {
          alert('Cannot remove primary email');
          return;
        }

        await api.delete('/remove-email', {
          data: {
            primary_ind_id: this.userData.ind_id,
            email: email,
            modified_by: this.userData.ind_id
          }
        });

        // Update UI
        this.emails = this.emails.filter(e => e.email !== email);
        if (this.selectedBackup === email) {
          this.selectedBackup = this.emails.find(e => !e.primary)?.email || '';
        }

        alert('Email removed successfully');

      } catch (error) {
        console.error('Error removing email:', error);
        alert('Failed to remove email');
      } finally {
        this.isLoading = false;
      }
    },

    resetEmailForm() {
      this.emailInput = '';
      this.toggle = false;
    }
  }
};
</script>
<template>
  <div class="email-box">
    <hr>
    <h4>Add or Manage Backup/Linked Emails</h4>
    <hr />

    <div class="current-email" v-if="primaryEmail">
      <strong>Primary Email:</strong> {{ primaryEmail }}
    </div>

    <!-- <div class="current-email" v-if="selectedBackup"> -->
    <!-- <strong>Current Backup Email:</strong> {{ currentBackupEmail }} -->
    <!-- <span v-if="verifiedEmails.some(e => e.email === selectedBackup)">✅</span> -->
    <!-- </div> -->

    <div class="email-section" v-if="verifiedEmails.length">
      <h4>Verified Emails</h4>
      <label v-for="e in verifiedEmails" :key="e.email" class="email-label"
        :class="{ 'selected-email': e.email === primaryEmail }">
        <input type="radio" name="primaryEmail" :checked="e.primary" @click="handleSetPrimaryEmail(e.email)"
          :disabled="isLoading || e.primary" />
        {{ e.email }}
        <span v-if="e.primary">(Current Primary)</span>
      </label>
    </div>

    <div class="email-section" v-if="unverifiedEmails.length">
      <h4>Unverified Emails</h4>
      <div v-for="e in unverifiedEmails" :key="e.email" class="unverified-email">
        <div class="email-info">
          {{ e.email }}
          <span class="tag-unverified">(Unverified)</span>
          <span v-if="e.verificationSent" class="verification-info">
            Sent on {{ new Date(e.verificationDate).toLocaleString() }}
          </span>
        </div>
        <div class="email-actions">
          <button @click="handleResend(e.email)" :disabled="isLoading">
            <span v-if="loadingMessage.includes('resend') && loadingMessage.includes(e.email)">
              Sending...
            </span>
            <span v-else>
              Resend Verification
            </span>
          </button>
          <button @click="handleRemove(e.email)" :disabled="isLoading">
            Unlink
          </button>
        </div>
      </div>
    </div>

    <div class="add-email">
      <ButtonSubmit v-show="!toggle" label="Add New Email" @click.prevent="showForm" :disabled="isLoading" />
      <form v-show="toggle" @submit.prevent="handleAddEmail">
        <input type="email" v-model="emailInput" placeholder="Enter new email" required :disabled="isLoading" />

        <!-- Helper text -->
        <small class="helper-text">
          If the email address isn't already registered, we'll create a linked account for that user automatically.
        </small>

        <div class="actions">
          <ButtonSubmit label="Submit" type="submit" :style="{ color: brandData.button_text_color || '#F47820' }"
            :disabled="isLoading" />
          <ButtonCancel label="Cancel" @click.prevent="showForm" :disabled="isLoading" />
        </div>
      </form>

    </div>
  </div>
</template>

<style scoped>
.email-box {
  margin: 20px 0;
  max-width: 50%;
}

.email-section {
  margin-top: 16px;
}

.email-label {
  display: flex;
  align-items: center;
  margin: 6px 0;
  cursor: pointer;
  gap: 8px;
}

.unverified-email {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0;
}

.tag-unverified {
  color: red;
}

.add-email {
  margin-top: 24px;
}

.actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

input[type="email"],
input[type="password"] {
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  margin-top: 8px;
  border: 1px solid grey;
  border-radius: 4px;
}

.current-email {
  background: #f0f0f0;
  padding: 10px;
  border-left: 4px solid #4CAF50;
  margin-bottom: 16px;
  font-weight: bold;
}

.password-options {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.unverified-email {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin: 8px 0;
  flex-wrap: wrap;
}

.email-info {
  flex-grow: 1;
  min-width: 200px;
}

.email-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.verification-info {
  font-size: 0.8em;
  color: #666;
  margin-left: 8px;
  display: block;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.unverified-email {
  padding: 12px;
  border: 1px solid #eee;
  border-radius: 4px;
  margin-bottom: 8px;
  background: #fafafa;
}

.email-actions button {
  padding: 6px 12px;
  font-size: 0.9em;
  border-radius: 4px;
  cursor: pointer;
}

.email-actions button:first-child {
  background: #f0f7ff;
  border: 1px solid #d0e3ff;
  color: #0066cc;
}

.email-actions button:last-child {
  background: #fff0f0;
  border: 1px solid #ffd0d0;
  color: #cc0000;
}

.email-label {
  color: inherit;
  /* Default text color */
  transition: color 0.2s;
}

.email-label.selected-email {
  color: #0066cc;
  /* Blue color for selected */
  font-weight: bold;
}

/* Style the radio button itself */
input[type="radio"] {
  margin-right: 8px;
}

/* Style for disabled radio buttons */
input[type="radio"]:disabled {
  cursor: not-allowed;
}
.helper-text {
  display: block;
  margin-top: 6px;
  font-size: 0.85rem;
  color: #666;
}

</style>